import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { CalendarIcon, Loader2, Send, User, Phone, MapPin, Clock, Check, MessageCircle, ShieldCheck } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { useI18n } from "../i18n";
import { useBooking } from "../lib/booking-context";
import { useCurrency } from "../lib/currency-context";
import { createBooking } from "../lib/api";
import { cn } from "../lib/utils";

const emptyForm = { name: "", phone: "", from_location: "", to_location: "", time: "", passengers: "2", luggage: "small", comment: "", category: "other", route: "" };

export const BookingForm = () => {
  const { t, lang } = useI18n();
  const { prefill } = useBooking();
  const { fmt } = useCurrency();
  const [form, setForm] = useState(emptyForm);
  const [date, setDate] = useState(null);
  const [website, setWebsite] = useState("");
  const [extra, setExtra] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (prefill && prefill._ts) {
      setForm((f) => ({
        ...f,
        from_location: prefill.from_location ?? f.from_location,
        to_location: prefill.to_location ?? f.to_location,
        category: prefill.category ?? f.category,
        route: prefill.route ?? f.route,
        comment: prefill.comment ? (f.comment ? `${f.comment}. ${prefill.comment}` : prefill.comment) : f.comment,
      }));
      setExtra({ estimated_price: prefill.estimated_price, distance_km: prefill.distance_km });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill?._ts]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.from_location.trim() || !form.to_location.trim()) {
      toast.error(t("form.required")); return;
    }
    setBusy(true);
    try {
      const payload = { ...form, date: date ? format(date, "yyyy-MM-dd") : "", lang, website, estimated_price: extra.estimated_price ?? null, distance_km: extra.distance_km ?? null };
      const res = await createBooking(payload);
      toast.success(res.telegram_status === "sent" ? t("form.successTelegram") : t("form.success"));
      setForm(emptyForm); setDate(null); setExtra({});
      const el = document.getElementById("booking-success"); if (el) el.dataset.visible = "true";
    } catch {
      toast.error(t("form.fail"));
      const el = document.getElementById("booking-error"); if (el) el.dataset.visible = "true";
    } finally { setBusy(false); }
  };

  const reassure = [t("reassure.r1"), t("reassure.r2"), t("reassure.r3")];

  return (
    <section id="book" className="relative py-14 sm:py-18 lg:py-24 overflow-hidden" data-testid="booking-section">
      <div className="mesh-glow top-0 right-[-12rem]" />
      <div className="relative max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-[-0.01em]">{t("form.title")}</h2>
          <p className="mt-3 text-muted-foreground">{t("form.subtitle")}</p>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
          {/* Form */}
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="lg:col-span-8 rounded-[18px] border border-border glass edge-light p-6 sm:p-8">
            <form onSubmit={submit} data-testid="booking-form" noValidate>
              <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)} name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                <div>
                  <Label className="text-sm text-muted-foreground">{t("form.name")} *</Label>
                  <div className="relative mt-1.5"><User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-accent" /><Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={t("form.namePh")} className="pl-9 rounded-xl h-11 focus-gold bg-background/50" data-testid="booking-form-name-input" /></div>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">{t("form.phone")} *</Label>
                  <div className="relative mt-1.5"><Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-accent" /><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder={t("form.phonePh")} className="pl-9 rounded-xl h-11 focus-gold bg-background/50" data-testid="booking-form-phone-input" /></div>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">{t("form.from")} *</Label>
                  <div className="relative mt-1.5"><MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-accent" /><Input value={form.from_location} onChange={(e) => set("from_location", e.target.value)} placeholder={t("form.fromPh")} className="pl-9 rounded-xl h-11 focus-gold bg-background/50" data-testid="booking-form-from-input" /></div>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">{t("form.to")} *</Label>
                  <div className="relative mt-1.5"><MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-accent" /><Input value={form.to_location} onChange={(e) => set("to_location", e.target.value)} placeholder={t("form.toPh")} className="pl-9 rounded-xl h-11 focus-gold bg-background/50" data-testid="booking-form-to-input" /></div>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">{t("form.date")}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" className={cn("mt-1.5 w-full justify-start rounded-xl h-11 font-normal bg-background/50 border-border", !date && "text-muted-foreground")} data-testid="booking-form-date-input">
                        <CalendarIcon className="mr-2 h-4 w-4" />{date ? format(date, "PPP") : t("form.pickDate")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-popover border-border" align="start">
                      <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">{t("form.time")}</Label>
                  <div className="relative mt-1.5"><Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-accent z-10" /><Input type="time" value={form.time} onChange={(e) => set("time", e.target.value)} className="pl-9 rounded-xl h-11 focus-gold bg-background/50" data-testid="booking-form-time-input" /></div>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">{t("form.passengers")}</Label>
                  <Select value={form.passengers} onValueChange={(v) => set("passengers", v)}>
                    <SelectTrigger className="mt-1.5 rounded-xl h-11 bg-background/50" data-testid="booking-form-passengers-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{["1","2","3","4","5","6","7","8"].map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">{t("form.luggage")}</Label>
                  <Select value={form.luggage} onValueChange={(v) => set("luggage", v)}>
                    <SelectTrigger className="mt-1.5 rounded-xl h-11 bg-background/50" data-testid="booking-form-luggage-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t("form.lug.none")}</SelectItem>
                      <SelectItem value="small">{t("form.lug.small")}</SelectItem>
                      <SelectItem value="medium">{t("form.lug.medium")}</SelectItem>
                      <SelectItem value="large">{t("form.lug.large")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-sm text-muted-foreground">{t("form.catSel")}</Label>
                  <Select value={form.category} onValueChange={(v) => set("category", v)}>
                    <SelectTrigger className="mt-1.5 rounded-xl h-11 bg-background/50" data-testid="booking-form-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="city">{t("form.cats.city")}</SelectItem>
                      <SelectItem value="airport">{t("form.cats.airport")}</SelectItem>
                      <SelectItem value="long_distance">{t("form.cats.long_distance")}</SelectItem>
                      <SelectItem value="other">{t("form.cats.other")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-sm text-muted-foreground">{t("form.comment")}</Label>
                  <Textarea value={form.comment} onChange={(e) => set("comment", e.target.value)} placeholder={t("form.commentPh")} rows={3} className="mt-1.5 rounded-xl focus-gold bg-background/50" data-testid="booking-form-comment-textarea" />
                </div>
              </div>

              {extra.estimated_price ? (
                <div className="mt-4 rounded-xl bg-background/50 border border-border px-4 py-3 text-sm">
                  <span className="text-muted-foreground">{t("calc.price")}: </span>
                  <span className="font-display font-semibold gold-text tabular-nums">{fmt(extra.estimated_price)}</span>
                  {extra.distance_km ? <span className="text-muted-foreground"> · {extra.distance_km} km</span> : null}
                </div>
              ) : null}

              <Button type="submit" size="lg" disabled={busy} className="mt-6 w-full rounded-xl shimmer active:scale-[0.98] h-12" data-testid="booking-form-submit-button">
                {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("form.sending")}</> : <><Send className="mr-2 h-4 w-4" /> {t("form.submit")}</>}
              </Button>
              <p id="booking-success" data-visible="false" data-testid="booking-form-success-message" className="mt-3 text-sm text-[hsl(var(--success))] text-center data-[visible=false]:hidden">{t("form.success")}</p>
              <p id="booking-error" data-visible="false" data-testid="booking-form-error-message" className="mt-3 text-sm text-destructive text-center data-[visible=false]:hidden">{t("form.fail")}</p>
            </form>
          </motion.div>

          {/* Reassurance panel */}
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }} className="lg:col-span-4">
            <div className="rounded-[18px] border border-border glass edge-light p-6 h-full flex flex-col">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-secondary ring-1 ring-[hsl(42_50%_45%/0.25)]"><ShieldCheck className="h-5 w-5 text-accent" /></span>
              <h3 className="mt-4 font-display text-xl font-semibold">{t("reassure.title")}</h3>
              <ul className="mt-4 space-y-3">
                {reassure.map((r, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-foreground/85">
                    <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-accent/15 text-accent shrink-0"><Check className="h-3 w-3" /></span>
                    {r}
                  </li>
                ))}
              </ul>
              <div className="gold-rule my-6" />
              <div className="flex items-center gap-2 text-sm text-accent"><Clock className="h-4 w-4" /> {t("reassure.response")}</div>
              <div className="mt-4 flex items-center gap-3">
                <a href="https://t.me/" target="_blank" rel="noreferrer" className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-secondary hover:bg-accent hover:text-accent-foreground transition-colors" aria-label="Telegram"><Send className="h-5 w-5" /></a>
                <a href="https://wa.me/420728992513" target="_blank" rel="noreferrer" className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-secondary hover:bg-accent hover:text-accent-foreground transition-colors" aria-label="WhatsApp"><MessageCircle className="h-5 w-5" /></a>
              </div>
              <p className="mt-auto pt-6 text-xs text-muted-foreground leading-relaxed">{t("reassure.privacy")}</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
